import { create } from "zustand";

import { api } from "../api/client";
import { mensajeDeError } from "../api/mensajes";
import type { Session } from "../api/types";

// El store guarda la sesión que devuelve el backend, no una copia de verdad.
// La autoridad es /auth/me: el token vive en una cookie HttpOnly que este
// código no puede leer (ADR-019), así que "estoy autenticado" siempre es una
// respuesta del servidor y no un booleano local.
type SessionState = {
  session: Session | null;
  status: "loading" | "authenticated" | "anonymous";
  error: string | null;
  /**
   * La API exigió segundo factor y el portal rebotó a Seguridad.
   *
   * Vive en el store y no en el estado de la navegación porque la persona
   * puede recargar o moverse, y seguir sin saber por qué ninguna pantalla
   * carga. Sin esto, el rebote es mudo: se abre el portal, se ve Seguridad y
   * no hay nada que explique qué pasó.
   */
  mfaRequerido: boolean;

  load: () => Promise<void>;
  /**
   * `totpCode` va vacío en el primer intento.
   *
   * El portal no sabe quién tiene segundo factor hasta que el backend lo dice
   * —preguntarlo antes de la contraseña revelaría qué cuentas existen—, así
   * que se manda sin código y se vuelve a enviar con él si responde
   * `MFA_REQUIRED`.
   */
  login: (email: string, password: string, totpCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  switchTenant: (tenantId: string) => Promise<void>;
  can: (permission: string) => boolean;
  marcarMFARequerido: () => void;
};

export const useSession = create<SessionState>((set, get) => ({
  session: null,
  status: "loading",
  error: null,
  mfaRequerido: false,

  marcarMFARequerido: () => set({ mfaRequerido: true }),

  load: async () => {
    try {
      const session = await api.get<Session>("/auth/me");
      set({ session, status: "authenticated", error: null });
    } catch {
      // Cualquier fallo al cargar la sesión significa "no autenticado": no
      // hay un estado intermedio útil para la UI.
      set({ session: null, status: "anonymous", error: null });
    }
  },

  login: async (email, password, totpCode) => {
    set({ error: null });
    try {
      const code = totpCode?.trim() ?? "";
      const session = await api.post<Session>("/auth/login", {
        email,
        password,
        // El campo se omite si no hay código: mandar una cadena vacía sería
        // decirle al backend "este es mi segundo factor", y no lo es.
        ...(code === "" ? {} : { totp_code: code }),
      });
      set({ session, status: "authenticated", error: null });
    } catch (err) {
      const message = mensajeDeError(err, "No se pudo iniciar sesión.");
      set({ error: message, status: "anonymous", session: null });
      throw err;
    }
  },

  logout: async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      // Se limpia el estado local pase lo que pase: si el servidor falló, el
      // usuario igual quiere salir.
      set({ session: null, status: "anonymous", error: null });
    }
  },

  switchTenant: async (tenantId) => {
    const session = await api.post<Session>("/auth/switch-tenant", { tenant_id: tenantId });
    set({ session, status: "authenticated" });
  },

  // La UI refleja permisos; la autorización real vive en el backend
  // (CLAUDE.md §6: ocultar un botón no es autorizar).
  can: (permission) => get().session?.permissions.includes(permission) ?? false,
}));
